import React from 'react'
import { Button, TextField, Stack, Box, Modal, Typography } from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import {
  useCreateDealerTypeMutation,
  useUpdateDealerTypeDetailsMutation,
} from '../../redux/api/dealerType'

interface Props {
  leadType?: any
  onClose: () => void
  refetch: () => void
  open: boolean
}

const validationSchema = yup.object({
  dealerType: yup.string().required('Dealer Type is required'),
  contractCreateApi: yup.string().required('Contract Create API is required'),
})

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    p: 2,
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    mb: 4,
  },
}

export const DealerTypeForm = ({ onClose, refetch, leadType, open }: Props) => {
  const [createModuleType, { isLoading, isError, error, isSuccess }] = useCreateDealerTypeMutation()
  const [
    updateDealerTypeDetails,
    { isLoading: uIsLoading, isError: uIsError, error: uError, isSuccess: uIsSuccess },
  ] = useUpdateDealerTypeDetailsMutation()

  const formik = useFormik({
    initialValues: {
      dealerType: leadType?.dealerType ?? '',
      contractCreateApi: leadType?.createContractAPI ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      const isUpdating = !!leadType?.dealerTypeId

      const requestData = {
        dealerType: values.dealerType,
        createContractAPI: values.contractCreateApi,
      }

      if (isUpdating) {
        updateDealerTypeDetails({
          dealerTypeId: leadType.dealerTypeId,
          application: 'Intranet',
          ...requestData,
        })
      } else {
        createModuleType({
          application: 'Intranet',
          ...requestData,
        })
      }
    },
  })

  React.useEffect(() => {
    if (isSuccess) {
      toast.success('Success!')
      formik.resetForm()
      onClose()
      refetch()
    }
    if (isError) {
      if (Array.isArray((error as any)?.data?.error)) {
        const errors = error as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((error as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [isLoading])

  React.useEffect(() => {
    if (uIsSuccess) {
      toast.success('Success!')
      formik.resetForm()
      onClose()
      refetch()
    }
    if (uIsError) {
      if (Array.isArray((uError as any)?.data?.error)) {
        const errors = uError as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((uError as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [uIsLoading])

  React.useEffect(() => {
    formik.setValues({
      dealerType: leadType?.dealerType ?? '',
      contractCreateApi: leadType?.createContractAPI ?? '',
    })
  }, [leadType])

  const reset = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='white'>
            Dealer Type Details
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                id='dealerType'
                name='dealerType'
                label='Dealer Type'
                autoComplete='off'
                value={formik.values.dealerType}
                onChange={formik.handleChange}
                error={formik.touched.dealerType && Boolean(formik.errors.dealerType)}
                helperText={
                  formik.touched.dealerType && formik.errors.dealerType
                    ? String(formik.errors.dealerType)
                    : undefined
                }
              />
              <TextField
                fullWidth
                id='contractCreateApi'
                name='contractCreateApi'
                label='Contract Create API'
                autoComplete='off'
                value={formik.values.contractCreateApi}
                onChange={formik.handleChange}
                error={formik.touched.contractCreateApi && Boolean(formik.errors.contractCreateApi)}
                helperText={
                  formik.touched.contractCreateApi && formik.errors.contractCreateApi
                    ? String(formik.errors.contractCreateApi)
                    : undefined
                }
              />
            </Stack>
            <Box sx={{ textAlign: 'right', mt: '30px' }}>
              <Button
                variant='outlined'
                size='large'
                sx={{ mr: 2, borderRadius: '20px', textDecoration: 'none' }}
                color='error'
                onClick={reset}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                sx={{ borderRadius: '20px', textDecoration: 'none' }}
                size='large'
                type='submit'
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  )
}
